import React, { useState, useEffect } from 'react';
import MetricCard from '../MetricCard';
import UserListTable from './UserListTable';
import UserDetailView from './UserDetailView';
import MultiSelectCombobox from '../../common/MultiSelectCombobox';
import { getFirestore, collection, getDocs, query, where, getDoc, doc, getCountFromServer, Timestamp } from 'firebase/firestore';
import { toast } from 'react-hot-toast';
import { useAuthContext } from '../../../hooks/useAuthContext';
import { isCurrentUserAdmin } from '../../../utils/adminUtils';

/**
 * UserManagementView component for admin dashboard
 * Shows a list of all users from various sources with their details
 * 
 * Improvements:
 * - Better naming for users without profiles by using email prefix instead of "Unnamed User"
 * - Display data from user profiles, profile summaries, and waitlist entries
 * - Sortable columns for registration date and last login
 */

// Helper function to safely format timestamps
const formatTimestamp = (timestamp) => {
  if (!timestamp) return 'Never';

  // Check if it's a Firebase timestamp (has seconds and nanoseconds)
  if (timestamp.seconds && timestamp.nanoseconds) {
    return new Date(timestamp.seconds * 1000).toLocaleString();
  }

  // Check if it's an ISO string
  if (typeof timestamp === 'string') {
    try {
      return new Date(timestamp).toLocaleString();
    } catch (e) {
      return timestamp;
    }
  }

  return String(timestamp);
};

// Helper function to convert timestamp to Date object for sorting
const timestampToDate = (timestamp) => {
  if (!timestamp) return null;

  // Handle Firebase timestamp
  if (timestamp.seconds && timestamp.nanoseconds) {
    return new Date(timestamp.seconds * 1000);
  }

  // Handle date strings in format "M/D/YYYY, h:mm:ss AM/PM"
  if (typeof timestamp === 'string') {
    // Handle special values
    if (timestamp === 'Never' || timestamp === 'Unknown') return null;

    try {
      // Try to parse the date string directly
      return new Date(timestamp);
    } catch (e) {
      console.warn("Failed to parse date string:", timestamp, e);
      return null;
    }
  }

  return null;
};

// Helper function to check if a date is within the last 24 hours
const isWithin24Hours = (timestamp) => {
  if (!timestamp) return false;

  // Handle Firebase timestamp
  if (timestamp.seconds && timestamp.nanoseconds) {
    const date = new Date(timestamp.seconds * 1000);
    const now = new Date();
    const hoursDiff = (now - date) / (1000 * 60 * 60);
    return hoursDiff <= 24;
  }

  // Handle date strings in format "M/D/YYYY, h:mm:ss AM/PM"
  if (typeof timestamp === 'string') {
    // Handle special values
    if (timestamp === 'Never' || timestamp === 'Unknown') return false;

    try {
      // Try to parse the date string
      const date = new Date(timestamp);
      const now = new Date();
      const hoursDiff = (now - date) / (1000 * 60 * 60);
      return hoursDiff <= 24;
    } catch (e) {
      return false;
    }
  }

  return false;
};

// Helper function to convert users data to CSV format
const convertToCSV = (users) => {
  if (users.length === 0) return '';

  // Define CSV headers
  const headers = [
    'Display Name',
    'Email',
    'Status',
    'Location',
    'LinkedIn URL',
    'Job Search Status',
    'Current Role',
    'Last Login',
    'Created At',
    'User ID'
  ];

  // Helper to escape CSV values
  const escapeCSV = (value) => {
    if (value === null || value === undefined) return '';
    const stringValue = String(value);
    // Escape double quotes and wrap in quotes if contains comma, newline, or quote
    if (stringValue.includes(',') || stringValue.includes('\n') || stringValue.includes('"')) {
      return `"${stringValue.replace(/"/g, '""')}"`;
    }
    return stringValue;
  };

  // Create CSV rows
  const rows = users.map(user => {
    const jobSearchStatus = user.preferences?.jobSearchStatus || user.jobSearchStatus || 'not-interested';
    const currentRole = user.basicInfo?.currentRole || user.currentRole || '';
    
    return [
      escapeCSV(user.displayName),
      escapeCSV(user.email),
      escapeCSV(user.status),
      escapeCSV(user.location),
      escapeCSV(user.linkedinUrl),
      escapeCSV(jobSearchStatus),
      escapeCSV(currentRole),
      escapeCSV(user.lastLogin),
      escapeCSV(user.createdAt),
      escapeCSV(user.id)
    ].join(',');
  });

  // Combine headers and rows
  return [headers.join(','), ...rows].join('\n');
};

export default function UserManagementView() {
  const { user } = useAuthContext();
  const [metrics, setMetrics] = useState({
    newWaitlistCount30Days: null,
    newWaitlistCount24Hours: null,
    newUserCount30Days: null,
    newUserCount24Hours: null,
    usersWithForumPosts: { count: null, total: null, percentage: null }
  });
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(true);
  const [loadingMetrics, setLoadingMetrics] = useState(true);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [selectedUserData, setSelectedUserData] = useState(null);
  const [loadingUserDetail, setLoadingUserDetail] = useState(false);
  const [sortField, setSortField] = useState('lastLogin'); // Default to sort by last login
  const [sortDirection, setSortDirection] = useState('desc'); // Default to most recent first
  const [recentUsers, setRecentUsers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [showNewUsersOnly, setShowNewUsersOnly] = useState(false);
  const [showOrphanedUsers, setShowOrphanedUsers] = useState(false);
  const [showArchivedUsers, setShowArchivedUsers] = useState(false);
  // Column filters - all now multi-select arrays for OR logic
  const [locationFilter, setLocationFilter] = useState([]);
  const [jobSearchStatusFilter, setJobSearchStatusFilter] = useState([]);
  const [hasLinkedInFilter, setHasLinkedInFilter] = useState([]);
  const [statusFilter, setStatusFilter] = useState([]);

  // Fetch metrics data
  useEffect(() => {
    const fetchMetrics = async () => {
      setLoadingMetrics(true);
      const db = getFirestore();
      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
      const thirtyDaysAgoTimestamp = Timestamp.fromDate(thirtyDaysAgo);

      const oneDayAgo = new Date();
      oneDayAgo.setDate(oneDayAgo.getDate() - 1);
      const oneDayAgoTimestamp = Timestamp.fromDate(oneDayAgo);

      try {
        const usersRef = collection(db, 'user_profiles');

        // Get all users created in last 30 days
        const newUsersQuery30Days = query(usersRef, where('createdAt', '>=', thirtyDaysAgoTimestamp));
        const newUsersSnapshot30Days = await getDocs(newUsersQuery30Days);

        let newWaitlistCount30Days = 0;
        let newUserCount30Days = 0;

        newUsersSnapshot30Days.forEach((doc) => {
          const userData = doc.data();
          if (userData.status === 'waitlist') {
            newWaitlistCount30Days++;
          } else {
            newUserCount30Days++;
          }
        });

        // Get all users created in last 24 hours
        const newUsersQuery24Hours = query(usersRef, where('createdAt', '>=', oneDayAgoTimestamp));
        const newUsersSnapshot24Hours = await getDocs(newUsersQuery24Hours);

        let newWaitlistCount24Hours = 0;
        let newUserCount24Hours = 0;

        newUsersSnapshot24Hours.forEach((doc) => {
          const userData = doc.data();
          if (userData.status === 'waitlist') {
            newWaitlistCount24Hours++;
          } else {
            newUserCount24Hours++;
          }
        });

        // 5. Users with Forum Posts (questions or replies)
        // First get total count of users
        const totalUsersSnapshot = await getCountFromServer(usersRef);
        const totalUsers = totalUsersSnapshot.data().count;

        // Get count of users who have posted in the forum (questions or replies)
        const forumQuestionsRef = collection(db, 'forum_questions');
        const forumQuestionsSnapshot = await getDocs(forumQuestionsRef);

        // Get unique user IDs who have posted
        const usersWithPosts = new Set();
        forumQuestionsSnapshot.forEach((doc) => {
          const postData = doc.data();
          if (postData.authorId) {
            usersWithPosts.add(postData.authorId);
          }
        });

        const usersWithForumPostsCount = usersWithPosts.size;

        // Calculate percentage
        const forumPostsPercentage = totalUsers > 0
          ? Math.round((usersWithForumPostsCount / totalUsers) * 100)
          : 0;

        setMetrics({
          newWaitlistCount30Days,
          newWaitlistCount24Hours,
          newUserCount30Days,
          newUserCount24Hours,
          usersWithForumPosts: {
            count: usersWithForumPostsCount,
            total: totalUsers,
            percentage: forumPostsPercentage
          }
        });
      } catch (error) {
        console.error("Error fetching admin metrics:", error);
        toast.error(`Failed to load metrics: ${error.message}`);
        setMetrics({
          newWaitlistCount30Days: 'Error',
          newWaitlistCount24Hours: 'Error',
          newUserCount30Days: 'Error',
          newUserCount24Hours: 'Error',
          usersWithForumPosts: { count: 'Error', total: 'Error', percentage: 'Error' }
        });
      } finally {
        setLoadingMetrics(false);
      }
    };

    fetchMetrics();
  }, []);

  // Fetch users list - comprehensive approach checking multiple collections
  useEffect(() => {
    const fetchUsers = async () => {
      setLoadingUsers(true);
      try {
        console.log("Fetching all user data...");
        const db = getFirestore();

        // Track unique users to avoid duplicates
        const userIds = [];
        const usersData = [];

        // 1. Fetch from user_profiles collection (primary source)
        console.log("Fetching from user_profiles collection...");
        const userProfilesSnapshot = await getDocs(collection(db, 'user_profiles'));
        console.log("User profiles found:", userProfilesSnapshot.docs.length);

        // Process user profiles data
        userProfilesSnapshot.docs.forEach(doc => {
          const data = doc.data();
          console.log("User profile:", doc.id, data);

          // Try to get a proper name using multiple field paths (including old structures)
          let displayName = 'Name not available';
          if (data.basicInfo?.firstName && data.basicInfo?.lastName) {
            // Current structure: nested in basicInfo
            displayName = `${data.basicInfo.firstName} ${data.basicInfo.lastName}`;
          } else if (data.firstName && data.lastName) {
            // Old structure: top-level firstName/lastName
            displayName = `${data.firstName} ${data.lastName}`;
          } else if (data.basicInfo?.name) {
            displayName = data.basicInfo.name;
          } else if (data.name) {
            // Old structure: top-level name
            displayName = data.name;
          } else if (data.displayName) {
            displayName = data.displayName;
          } else if (data.basicInfo?.email) {
            // Use email prefix as fallback name
            const emailName = data.basicInfo.email.split('@')[0];
            displayName = emailName.charAt(0).toUpperCase() + emailName.slice(1);
          } else if (data.email) {
            // Old structure: top-level email
            const emailName = data.email.split('@')[0];
            displayName = emailName.charAt(0).toUpperCase() + emailName.slice(1);
          }

          // Check if user is archived
          const isArchived = data.isArchived || false;

          // If archived, append "(Archived)" to the name
          if (isArchived) {
            displayName = `${displayName} (Archived)`;
          }

          // Get recommended jobs if they exist
          const recommendedJobs = data.recommendedJobs || [];

          // Add user profile data
          const userData = {
            id: doc.id,
            email: data.basicInfo?.email || data.email || 'Unknown Email',
            displayName: displayName,
            status: data.status || 'active', // Include user status
            isAdmin: data.isAdmin || false,
            isArchived: isArchived,
            archivedAt: data.archivedAt || null,
            archivedBy: data.archivedBy || null,
            archivedByEmail: data.archivedByEmail || null,
            lastLogin: data.lastLogin ? formatTimestamp(data.lastLogin) : 'Never',
            createdAt: data.createdAt ? formatTimestamp(data.createdAt) : 'Unknown',
            hasUserProfile: true,
            waitlistSubmissionId: data.waitlistSubmissionId || null,
            hasWaitlistData: !!data.waitlistSubmissionId,
            videoCount: data.videoResponses ? Object.keys(data.videoResponses).length : 0,
            videoTotal: 5, // Total number of expected videos from QUESTIONS array
            recommendedJobs: recommendedJobs,
            // Include location and LinkedIn URL
            location: data.basicInfo?.location || '',
            linkedinUrl: data.basicInfo?.linkedinUrl || data.profileLinks?.linkedin || '',
            // Include profile pictures
            profilePicture: data.profilePicture || null,
            photoURL: data.photoURL || null,
            basicInfo: data.basicInfo || {},
            // Include current role
            currentRole: data.basicInfo?.currentRole || '',
            // Include preferences with default jobSearchStatus
            preferences: {
              ...data.preferences,
              jobSearchStatus: data.preferences?.jobSearchStatus || 'not-interested'
            },
            jobSearchStatus: data.preferences?.jobSearchStatus || 'not-interested'
          };

          userIds.push(doc.id);
          usersData.push(userData);
        });

        // 2. Also fetch users from user_profile_summaries to catch new users with empty profiles
        console.log("Fetching from user_profile_summaries collection...");
        const profileSummariesSnapshot = await getDocs(collection(db, 'user_profile_summaries'));
        console.log("Profile summaries found:", profileSummariesSnapshot.docs.length);

        // Also fetch waitlist data now so we can use it for names
        console.log("Also fetching from waitlist collection...");
        const waitlistSnapshot = await getDocs(collection(db, 'waitlist'));
        console.log("Waitlist documents:", waitlistSnapshot.docs.length);

        // Create a map of userId to waitlist data for quick lookup
        const waitlistByUserId = new Map();
        waitlistSnapshot.docs.forEach(doc => {
          const data = doc.data();
          if (data.userId) {
            waitlistByUserId.set(data.userId, { id: doc.id, ...data });
          }
        });

        profileSummariesSnapshot.docs.forEach(doc => {
          const data = doc.data();
          // Only add if not already in our list
          if (!userIds.includes(doc.id)) {
            console.log("Found user with profile summary but no user profile:", doc.id, data);

            // Look for an existing user profile with this ID to get the email
            const matchingUser = userProfilesSnapshot.docs.find(profileDoc => profileDoc.id === doc.id);
            const email = matchingUser ?
              (matchingUser.data().email || matchingUser.data().basicInfo?.email) :
              (data.userID || doc.id);

            // Check if this user has waitlist data
            const waitlistData = waitlistByUserId.get(doc.id);

            // Try to get the user's name from waitlist data first, then fall back to email
            let displayName = "Name not available";

            if (waitlistData) {
              // Try to extract name from waitlist data (check multiple structures)
              if (waitlistData.userInfo?.firstName && waitlistData.userInfo?.lastName) {
                displayName = `${waitlistData.userInfo.firstName} ${waitlistData.userInfo.lastName}`;
              } else if (waitlistData.firstName && waitlistData.lastName) {
                displayName = `${waitlistData.firstName} ${waitlistData.lastName}`;
              } else if (waitlistData.userInfo?.name) {
                displayName = waitlistData.userInfo.name;
              } else if (waitlistData.name) {
                displayName = waitlistData.name;
              } else if (waitlistData.userInfo?.email) {
                const emailName = waitlistData.userInfo.email.split('@')[0];
                displayName = emailName.charAt(0).toUpperCase() + emailName.slice(1);
              } else if (waitlistData.email) {
                const emailName = waitlistData.email.split('@')[0];
                displayName = emailName.charAt(0).toUpperCase() + emailName.slice(1);
              }
            } else if (email && email !== doc.id) {
              // If no waitlist data, create a display name from the email (first part)
              const emailName = email.split('@')[0];
              displayName = emailName.charAt(0).toUpperCase() + emailName.slice(1);
            }

            userIds.push(doc.id);
            usersData.push({
              id: doc.id,
              email: email,
              displayName: displayName,
              status: 'active', // Default status for users with only profile summaries
              hasProfileSummary: true,
              hasUserProfile: false,
              hasWaitlistData: !!waitlistData,
              waitlistSubmissionId: waitlistData?.id || null,
              // Default empty location and LinkedIn for profile summary users
              location: waitlistData?.userInfo?.location || waitlistData?.location || '',
              linkedinUrl: waitlistData?.userInfo?.linkedinUrl || waitlistData?.linkedinUrl || '',
              // Default preferences for users with profile summaries
              preferences: {
                jobSearchStatus: waitlistData?.quizAnswers?.jobSearchStatus || 'not-interested'
              },
              jobSearchStatus: waitlistData?.quizAnswers?.jobSearchStatus || 'not-interested'
            });
          }
        });

        // 3. Process remaining waitlist entries (already fetched above)
        // Now process users that are ONLY in waitlist (not in user_profiles or user_profile_summaries)
        waitlistSnapshot.docs.forEach(doc => {
          const data = doc.data();

          // Only add users that aren't already in our list
          if (data.userId && !userIds.includes(data.userId)) {
            userIds.push(data.userId);

            // Try to create a proper display name (check multiple structures)
            let displayName = 'Name not available';
            if (data.userInfo?.firstName && data.userInfo?.lastName) {
              displayName = `${data.userInfo.firstName} ${data.userInfo.lastName}`;
            } else if (data.firstName && data.lastName) {
              // Old structure: top-level firstName/lastName
              displayName = `${data.firstName} ${data.lastName}`;
            } else if (data.userInfo?.name) {
              displayName = data.userInfo.name;
            } else if (data.name) {
              // Old structure: top-level name
              displayName = data.name;
            } else if (data.userInfo?.email) {
              // Use email prefix as fallback
              const emailName = data.userInfo.email.split('@')[0];
              displayName = emailName.charAt(0).toUpperCase() + emailName.slice(1);
            } else if (data.email) {
              // Old structure: top-level email
              const emailName = data.email.split('@')[0];
              displayName = emailName.charAt(0).toUpperCase() + emailName.slice(1);
            }

            const userData = {
              id: data.userId,
              email: data.userInfo?.email || 'Unknown Email',
              displayName: displayName,
              status: 'waitlist', // Waitlist collection users are waitlist by default
              submissionId: doc.id,
              submissionDate: data.submittedAt ? formatTimestamp(data.submittedAt) :
                (data.createdAt ? formatTimestamp(data.createdAt) : 'Unknown'),
              hasWaitlistData: true,
              hasUserProfile: false,
              // Extract location and LinkedIn from userInfo if available
              location: data.userInfo?.location || '',
              linkedinUrl: data.userInfo?.linkedinUrl || '',
              // Default preferences for waitlist users
              preferences: {
                jobSearchStatus: data.quizAnswers?.jobSearchStatus || 'not-interested'
              },
              jobSearchStatus: data.quizAnswers?.jobSearchStatus || 'not-interested'
            };

            usersData.push(userData);
          } else if (!data.userId) {
            // Add orphaned submissions with no user ID
            // Try to create a proper display name (check multiple structures)
            let displayName = 'Name not available (Orphaned)';
            if (data.userInfo?.firstName && data.userInfo?.lastName) {
              displayName = `${data.userInfo.firstName} ${data.userInfo.lastName} (Orphaned)`;
            } else if (data.firstName && data.lastName) {
              // Old structure: top-level firstName/lastName
              displayName = `${data.firstName} ${data.lastName} (Orphaned)`;
            } else if (data.userInfo?.name) {
              displayName = `${data.userInfo.name} (Orphaned)`;
            } else if (data.name) {
              // Old structure: top-level name
              displayName = `${data.name} (Orphaned)`;
            } else if (data.userInfo?.email) {
              // Use email prefix as fallback
              const emailName = data.userInfo.email.split('@')[0];
              displayName = `${emailName.charAt(0).toUpperCase() + emailName.slice(1)} (Orphaned)`;
            } else if (data.email) {
              // Old structure: top-level email
              const emailName = data.email.split('@')[0];
              displayName = `${emailName.charAt(0).toUpperCase() + emailName.slice(1)} (Orphaned)`;
            }

            const userData = {
              id: doc.id, // Use the doc ID since there's no user ID
              email: data.userInfo?.email || 'Unknown Email',
              displayName: displayName,
              status: 'waitlist', // Orphaned waitlist submissions
              submissionId: doc.id,
              submissionDate: data.submittedAt ? formatTimestamp(data.submittedAt) :
                (data.createdAt ? formatTimestamp(data.createdAt) : 'Unknown'),
              isOrphaned: true,
              hasWaitlistData: true,
              hasUserProfile: false,
              // Extract location and LinkedIn from userInfo if available
              location: data.userInfo?.location || '',
              linkedinUrl: data.userInfo?.linkedinUrl || '',
              // Default preferences for orphaned users
              preferences: {
                jobSearchStatus: 'not-interested'
              },
              jobSearchStatus: 'not-interested'
            };

            // Don't add to userIds because this isn't a real user ID
            usersData.push(userData);
          }
        });

        console.log("Total users found:", usersData.length);
        setUsers(usersData);
      } catch (error) {
        console.error("Error fetching users:", error);
        toast.error(`Failed to load users: ${error.message}`);
      } finally {
        setLoadingUsers(false);
      }
    };

    fetchUsers();
  }, []);

  // When users are loaded, find recent users for filtering
  useEffect(() => {
    const recent = users.filter(user => isWithin24Hours(user.createdAt));
    setRecentUsers(recent);
  }, [users]);

  // Get unique values for filter dropdowns
  const getUniqueLocations = () => {
    const locations = users
      .map(user => user.location)
      .filter(location => location && location.trim() !== '')
      .filter((location, index, arr) => arr.indexOf(location) === index)
      .sort();
    return locations;
  };

  const getUniqueStatuses = () => {
    const statuses = users
      .map(user => user.status)
      .filter(status => status && status.trim() !== '')
      .filter((status, index, arr) => arr.indexOf(status) === index)
      .sort();
    return statuses;
  };

  // Update filtered users and check for recent users
  useEffect(() => {
    // Apply filters and search
    let filtered = [...users];

    // Filter by search term
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(user =>
        (user.displayName && user.displayName.toLowerCase().includes(term)) ||
        (user.id && user.id.toLowerCase().includes(term))
      );
    }

    // Filter by location (OR logic - show users matching any selected location)
    if (locationFilter.length > 0) {
      filtered = filtered.filter(user => locationFilter.includes(user.location));
    }

    // Filter by job search status (OR logic - show users matching any selected status)
    if (jobSearchStatusFilter.length > 0) {
      filtered = filtered.filter(user => {
        const userStatus = user.preferences?.jobSearchStatus || user.jobSearchStatus;
        return jobSearchStatusFilter.includes(userStatus);
      });
    }

    // Filter by LinkedIn presence (OR logic - show users matching any selected option)
    if (hasLinkedInFilter.length > 0) {
      filtered = filtered.filter(user => {
        const hasLinkedIn = user.linkedinUrl && user.linkedinUrl.trim() !== '';
        return hasLinkedInFilter.some(filter => {
          if (filter === 'yes') return hasLinkedIn;
          if (filter === 'no') return !hasLinkedIn;
          return false;
        });
      });
    }

    // Filter by status (OR logic - show users matching any selected status)
    if (statusFilter.length > 0) {
      filtered = filtered.filter(user => statusFilter.includes(user.status));
    }

    // Filter by new users only
    if (showNewUsersOnly) {
      filtered = filtered.filter(user => isWithin24Hours(user.createdAt));
    }

    // Hide orphaned users by default unless explicitly showing them
    if (!showOrphanedUsers) {
      filtered = filtered.filter(user => !user.isOrphaned);
    }

    // Hide archived users by default unless explicitly showing them
    if (!showArchivedUsers) {
      filtered = filtered.filter(user => !user.isArchived);
    }

    // Apply sorting
    filtered.sort((a, b) => {
      if (sortField === 'lastLogin') {
        // Get values to compare
        const aValue = a.lastLogin || '';
        const bValue = b.lastLogin || '';

        // Special handling for "Never" values - they should come last in ascending order
        if (aValue === 'Never' && bValue === 'Never') return 0;
        if (aValue === 'Never') return sortDirection === 'asc' ? 1 : -1;
        if (bValue === 'Never') return sortDirection === 'asc' ? -1 : 1;

        // Handle regular dates
        const dateA = timestampToDate(aValue);
        const dateB = timestampToDate(bValue);

        // If both dates are valid, compare them
        if (dateA && dateB) {
          return sortDirection === 'asc'
            ? dateA.getTime() - dateB.getTime()
            : dateB.getTime() - dateA.getTime();
        }

        // If only one date is valid
        if (dateA) return sortDirection === 'asc' ? -1 : 1;
        if (dateB) return sortDirection === 'asc' ? 1 : -1;

        // If no valid dates, fall back to string comparison
        return sortDirection === 'asc'
          ? String(aValue).localeCompare(String(bValue))
          : String(bValue).localeCompare(String(aValue));
      }

      if (sortField === 'createdAt') {
        // Get values to compare (account for both createdAt and submissionDate)
        const aValue = a.createdAt || a.submissionDate || '';
        const bValue = b.createdAt || b.submissionDate || '';

        // Special handling for "Unknown" values - they should come last in ascending order
        if (aValue === 'Unknown' && bValue === 'Unknown') return 0;
        if (aValue === 'Unknown') return sortDirection === 'asc' ? 1 : -1;
        if (bValue === 'Unknown') return sortDirection === 'asc' ? -1 : 1;

        // Handle regular dates
        const dateA = timestampToDate(aValue);
        const dateB = timestampToDate(bValue);

        // If both dates are valid, compare them
        if (dateA && dateB) {
          return sortDirection === 'asc'
            ? dateA.getTime() - dateB.getTime()
            : dateB.getTime() - dateA.getTime();
        }

        // If only one date is valid
        if (dateA) return sortDirection === 'asc' ? -1 : 1;
        if (dateB) return sortDirection === 'asc' ? 1 : -1;

        // If no valid dates, fall back to string comparison
        return sortDirection === 'asc'
          ? String(aValue).localeCompare(String(bValue))
          : String(bValue).localeCompare(String(aValue));
      }

      if (sortField === 'displayName') {
        // Custom sorting for displayName to put "Name not available" at the end
        const aValue = a.displayName || '';
        const bValue = b.displayName || '';

        // Check if either value is "Name not available" or contains it
        const aIsUnknown = aValue.startsWith('Name not available');
        const bIsUnknown = bValue.startsWith('Name not available');

        // If both are unknown, keep them together
        if (aIsUnknown && bIsUnknown) {
          return sortDirection === 'asc'
            ? String(aValue).localeCompare(String(bValue))
            : String(bValue).localeCompare(String(aValue));
        }

        // If only one is unknown, put it at the end regardless of sort direction
        if (aIsUnknown) return 1; // a goes after b
        if (bIsUnknown) return -1; // b goes after a

        // Both have real names, do normal string comparison
        return sortDirection === 'asc'
          ? String(aValue).localeCompare(String(bValue))
          : String(bValue).localeCompare(String(aValue));
      }

      if (sortField === 'jobSearchStatus') {
        // Custom sorting for job search status with priority order
        const statusOrder = {
          'actively-looking': 1,
          'open-to-opportunities': 2,
          'not-interested': 3
        };

        const aStatus = a.preferences?.jobSearchStatus || a.jobSearchStatus || 'not-interested';
        const bStatus = b.preferences?.jobSearchStatus || b.jobSearchStatus || 'not-interested';

        const aOrder = statusOrder[aStatus] || 3;
        const bOrder = statusOrder[bStatus] || 3;

        return sortDirection === 'asc'
          ? aOrder - bOrder
          : bOrder - aOrder;
      }

      // Default to string comparison for other fields
      const valueA = a[sortField] || '';
      const valueB = b[sortField] || '';

      return sortDirection === 'asc'
        ? String(valueA).localeCompare(String(valueB))
        : String(valueB).localeCompare(String(valueA));
    });

    setFilteredUsers(filtered);
  }, [users, searchTerm, locationFilter, jobSearchStatusFilter, hasLinkedInFilter, statusFilter, showNewUsersOnly, showOrphanedUsers, showArchivedUsers, sortField, sortDirection]);

  // Handle column sort change
  const handleSort = (field) => {
    // If clicking the same field, toggle direction
    if (field === sortField) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // If clicking a new field, set it as the sort field and default to ascending
      setSortField(field);
      setSortDirection('asc');
    }
  };

  // Handle user selection with comprehensive data fetching
  const handleUserSelect = async (userId) => {
    setSelectedUserId(userId);
    setLoadingUserDetail(true);

    try {
      const db = getFirestore();

      // First check if there's a user profile in user_profiles collection
      console.log("Checking for user profile in user_profiles collection");
      const userProfileRef = doc(db, 'user_profiles', userId);
      const userProfileSnap = await getDoc(userProfileRef);

      let userProfileData = null;
      let profileSummaryData = null;
      let waitlistData = null;
      let submissionId = null;

      if (userProfileSnap.exists()) {
        console.log("Found user profile:", userProfileSnap.data());
        userProfileData = userProfileSnap.data();
      }

      // Always check for profile summary data
      console.log("Checking for profile summary data");
      const profileSummaryRef = doc(db, 'user_profile_summaries', userId);
      const profileSummarySnap = await getDoc(profileSummaryRef);

      if (profileSummarySnap.exists()) {
        console.log("Found profile summary:", profileSummarySnap.data());
        profileSummaryData = profileSummarySnap.data();
      }

      // Check waitlist data
      if (userProfileData && userProfileData.waitlistSubmissionId) {
        submissionId = userProfileData.waitlistSubmissionId;
        const waitlistRef = doc(db, 'waitlist', submissionId);
        const waitlistSnap = await getDoc(waitlistRef);

        if (waitlistSnap.exists()) {
          waitlistData = waitlistSnap.data();
          console.log("Found waitlist data from user profile reference:", waitlistData);
        }
      } else {
        // If no waitlist submission ID in user profile, try to find by user ID
        const waitlistRef = collection(db, 'waitlist');
        const q = query(waitlistRef, where('userId', '==', userId));
        const querySnapshot = await getDocs(q);

        if (!querySnapshot.empty) {
          waitlistData = querySnapshot.docs[0].data();
          submissionId = querySnapshot.docs[0].id;
          console.log("Found waitlist data by user ID query:", waitlistData);
        }
      }

      // Try to create a proper display name using multiple sources (including old structures)
      let displayName = 'Name not available';

      if (userProfileData?.basicInfo?.firstName && userProfileData?.basicInfo?.lastName) {
        // Current structure: nested in basicInfo
        displayName = `${userProfileData.basicInfo.firstName} ${userProfileData.basicInfo.lastName}`;
      } else if (userProfileData?.firstName && userProfileData?.lastName) {
        // Old structure: top-level firstName/lastName
        displayName = `${userProfileData.firstName} ${userProfileData.lastName}`;
      } else if (userProfileData?.basicInfo?.name) {
        displayName = userProfileData.basicInfo.name;
      } else if (userProfileData?.name) {
        // Old structure: top-level name
        displayName = userProfileData.name;
      } else if (userProfileData?.displayName) {
        displayName = userProfileData.displayName;
      } else if (waitlistData?.userInfo?.firstName && waitlistData?.userInfo?.lastName) {
        displayName = `${waitlistData.userInfo.firstName} ${waitlistData.userInfo.lastName}`;
      } else if (waitlistData?.firstName && waitlistData?.lastName) {
        // Old structure: top-level firstName/lastName in waitlist
        displayName = `${waitlistData.firstName} ${waitlistData.lastName}`;
      } else if (waitlistData?.userInfo?.name) {
        displayName = waitlistData.userInfo.name;
      } else if (waitlistData?.name) {
        // Old structure: top-level name in waitlist
        displayName = waitlistData.name;
      } else if (userProfileData?.basicInfo?.email || userProfileData?.email) {
        // Use email prefix as fallback
        const email = userProfileData.basicInfo?.email || userProfileData.email;
        const emailName = email.split('@')[0];
        displayName = emailName.charAt(0).toUpperCase() + emailName.slice(1);
      } else if (waitlistData?.userInfo?.email || waitlistData?.email) {
        // Old structure: email fallback from waitlist
        const email = waitlistData.userInfo?.email || waitlistData.email;
        const emailName = email.split('@')[0];
        displayName = emailName.charAt(0).toUpperCase() + emailName.slice(1);
      }

      // Combine all data
      const userDetailData = {
        ...userProfileData,
        id: userId,
        displayName: displayName,
        email: userProfileData?.basicInfo?.email || userProfileData?.email || waitlistData?.userInfo?.email || 'Unknown Email',
        // Add profile summary data if available
        profileSummary: profileSummaryData?.profileSummary || '',
        keySkills: profileSummaryData?.keySkills || [],
        painPoints: profileSummaryData?.painPoints || [],
        // Add waitlist info if available
        waitlistData: waitlistData,
        submissionId: submissionId,
        // If fields exist
        hasUserProfile: !!userProfileData,
        hasProfileSummary: !!profileSummaryData,
        hasWaitlistData: !!waitlistData
      };

      setSelectedUserData(userDetailData);
    } catch (error) {
      console.error("Error fetching user details:", error);
      toast.error(`Failed to load user details: ${error.message}`);
      setSelectedUserData(null);
    } finally {
      setLoadingUserDetail(false);
    }
  };

  // Function to refresh the user list
  const refreshUsers = async () => {
    setLoadingUsers(true);
    try {
      console.log("Refreshing user data...");
      const db = getFirestore();

      // Use the same logic as in the useEffect fetchUsers function
      const userIds = [];
      const usersData = [];

      // 1. Fetch from user_profiles collection (primary source)
      console.log("Fetching from user_profiles collection...");
      const userProfilesSnapshot = await getDocs(collection(db, 'user_profiles'));
      console.log("User profiles found:", userProfilesSnapshot.docs.length);

      // Process user profiles data
      userProfilesSnapshot.docs.forEach(doc => {
        const data = doc.data();

        // Try to get a proper name using multiple field paths (including old structures)
        let displayName = 'Name not available';
        if (data.basicInfo?.firstName && data.basicInfo?.lastName) {
          // Current structure: nested in basicInfo
          displayName = `${data.basicInfo.firstName} ${data.basicInfo.lastName}`;
        } else if (data.firstName && data.lastName) {
          // Old structure: top-level firstName/lastName
          displayName = `${data.firstName} ${data.lastName}`;
        } else if (data.basicInfo?.name) {
          displayName = data.basicInfo.name;
        } else if (data.name) {
          // Old structure: top-level name
          displayName = data.name;
        } else if (data.displayName) {
          displayName = data.displayName;
        } else if (data.basicInfo?.email) {
          // Use email prefix as fallback name
          const emailName = data.basicInfo.email.split('@')[0];
          displayName = emailName.charAt(0).toUpperCase() + emailName.slice(1);
        } else if (data.email) {
          // Old structure: top-level email
          const emailName = data.email.split('@')[0];
          displayName = emailName.charAt(0).toUpperCase() + emailName.slice(1);
        }

        // Get recommended jobs if they exist
        const recommendedJobs = data.recommendedJobs || [];

        // Add user profile data
        const userData = {
          id: doc.id,
          email: data.basicInfo?.email || data.email || 'Unknown Email',
          displayName: displayName,
          status: data.status || 'active',
          isAdmin: data.isAdmin || false,
          isArchived: data.isArchived || false,
          archivedAt: data.archivedAt || null,
          archivedBy: data.archivedBy || null,
          archivedByEmail: data.archivedByEmail || null,
          lastLogin: data.lastLogin ? formatTimestamp(data.lastLogin) : 'Never',
          createdAt: data.createdAt ? formatTimestamp(data.createdAt) : 'Unknown',
          hasUserProfile: true,
          waitlistSubmissionId: data.waitlistSubmissionId || null,
          hasWaitlistData: !!data.waitlistSubmissionId,
          videoCount: data.videoResponses ? Object.keys(data.videoResponses).length : 0,
          videoTotal: 5, // Total number of expected videos from QUESTIONS array
          recommendedJobs: recommendedJobs,
          // Include location and LinkedIn URL
          location: data.basicInfo?.location || '',
          linkedinUrl: data.basicInfo?.linkedinUrl || data.profileLinks?.linkedin || '',
          // Include profile pictures
          profilePicture: data.profilePicture || null,
          photoURL: data.photoURL || null,
          basicInfo: data.basicInfo || {},
          // Include current role
          currentRole: data.basicInfo?.currentRole || '',
          // Include preferences with default jobSearchStatus
          preferences: {
            ...data.preferences,
            jobSearchStatus: data.preferences?.jobSearchStatus || 'not-interested'
          },
          jobSearchStatus: data.preferences?.jobSearchStatus || 'not-interested'
        };

        userIds.push(doc.id);
        usersData.push(userData);
      });

      // 2. Also fetch users from user_profile_summaries to catch new users with empty profiles
      console.log("Fetching from user_profile_summaries collection...");
      const profileSummariesSnapshot = await getDocs(collection(db, 'user_profile_summaries'));
      console.log("Profile summaries found:", profileSummariesSnapshot.docs.length);

      profileSummariesSnapshot.docs.forEach(doc => {
        const data = doc.data();
        // Only add if not already in our list
        if (!userIds.includes(doc.id)) {
          console.log("Found user with profile summary but no user profile:", doc.id, data);

          // Look for an existing user profile with this ID to get the email
          const matchingUser = userProfilesSnapshot.docs.find(profileDoc => profileDoc.id === doc.id);
          const email = matchingUser ?
            (matchingUser.data().email || matchingUser.data().basicInfo?.email) :
            (data.userID || doc.id);

          // Try to get the user's name from the Firebase Auth
          // Will make a server call in refreshUsers to fetch all Firebase Auth users
          // For now, try to use a better name than "Name not available"
          let displayName = "Name not available";

          // If we have an email, use that as a better name than just "Name not available"
          if (email && email !== doc.id) {
            // Create a display name from the email (first part)
            const emailName = email.split('@')[0];
            displayName = emailName.charAt(0).toUpperCase() + emailName.slice(1);
          }

          userIds.push(doc.id);
          usersData.push({
            id: doc.id,
            email: email,
            displayName: displayName,
            hasProfileSummary: true,
            hasUserProfile: false,
            hasWaitlistData: false,
            // Default empty location and LinkedIn for profile summary users
            location: '',
            linkedinUrl: '',
            // Default preferences for users with profile summaries
            preferences: {
              jobSearchStatus: 'not-interested'
            },
            jobSearchStatus: 'not-interested'
          });
        }
      });

      // 3. Get users from waitlist collection for complete coverage
      console.log("Also fetching from waitlist collection...");
      const waitlistSnapshot = await getDocs(collection(db, 'waitlist'));
      console.log("Waitlist documents:", waitlistSnapshot.docs.length);

      waitlistSnapshot.docs.forEach(doc => {
        const data = doc.data();

        // Only add users that aren't already in our list
        if (data.userId && !userIds.includes(data.userId)) {
          userIds.push(data.userId);

          // Try to create a proper display name (check multiple structures)
          let displayName = 'Name not available';
          if (data.userInfo?.firstName && data.userInfo?.lastName) {
            displayName = `${data.userInfo.firstName} ${data.userInfo.lastName}`;
          } else if (data.firstName && data.lastName) {
            // Old structure: top-level firstName/lastName
            displayName = `${data.firstName} ${data.lastName}`;
          } else if (data.userInfo?.name) {
            displayName = data.userInfo.name;
          } else if (data.name) {
            // Old structure: top-level name
            displayName = data.name;
          } else if (data.userInfo?.email) {
            // Use email prefix as fallback
            const emailName = data.userInfo.email.split('@')[0];
            displayName = emailName.charAt(0).toUpperCase() + emailName.slice(1);
          } else if (data.email) {
            // Old structure: top-level email
            const emailName = data.email.split('@')[0];
            displayName = emailName.charAt(0).toUpperCase() + emailName.slice(1);
          }

          const userData = {
            id: data.userId,
            email: data.userInfo?.email || 'Unknown Email',
            displayName: displayName,
            submissionId: doc.id,
            submissionDate: data.submittedAt ? formatTimestamp(data.submittedAt) :
              (data.createdAt ? formatTimestamp(data.createdAt) : 'Unknown'),
            hasWaitlistData: true,
            hasUserProfile: false,
            // Extract location and LinkedIn from userInfo if available
            location: data.userInfo?.location || '',
            linkedinUrl: data.userInfo?.linkedinUrl || '',
            // Default preferences for waitlist users
            preferences: {
              jobSearchStatus: data.quizAnswers?.jobSearchStatus || 'not-interested'
            },
            jobSearchStatus: data.quizAnswers?.jobSearchStatus || 'not-interested'
          };

          usersData.push(userData);
        } else if (!data.userId) {
          // Add orphaned submissions with no user ID
          // Try to create a proper display name (check multiple structures)
          let displayName = 'Name not available (Orphaned)';
          if (data.userInfo?.firstName && data.userInfo?.lastName) {
            displayName = `${data.userInfo.firstName} ${data.userInfo.lastName} (Orphaned)`;
          } else if (data.firstName && data.lastName) {
            // Old structure: top-level firstName/lastName
            displayName = `${data.firstName} ${data.lastName} (Orphaned)`;
          } else if (data.userInfo?.name) {
            displayName = `${data.userInfo.name} (Orphaned)`;
          } else if (data.name) {
            // Old structure: top-level name
            displayName = `${data.name} (Orphaned)`;
          } else if (data.userInfo?.email) {
            // Use email prefix as fallback
            const emailName = data.userInfo.email.split('@')[0];
            displayName = `${emailName.charAt(0).toUpperCase() + emailName.slice(1)} (Orphaned)`;
          } else if (data.email) {
            // Old structure: top-level email
            const emailName = data.email.split('@')[0];
            displayName = `${emailName.charAt(0).toUpperCase() + emailName.slice(1)} (Orphaned)`;
          }

          const userData = {
            id: doc.id, // Use the doc ID since there's no user ID
            email: data.userInfo?.email || 'Unknown Email',
            displayName: displayName,
            submissionId: doc.id,
            submissionDate: data.submittedAt ? formatTimestamp(data.submittedAt) :
              (data.createdAt ? formatTimestamp(data.createdAt) : 'Unknown'),
            isOrphaned: true,
            hasWaitlistData: true,
            hasUserProfile: false,
            // Extract location and LinkedIn from userInfo if available
            location: data.userInfo?.location || '',
            linkedinUrl: data.userInfo?.linkedinUrl || '',
            // Default preferences for orphaned users
            preferences: {
              jobSearchStatus: 'not-interested'
            },
            jobSearchStatus: 'not-interested'
          };

          // Don't add to userIds because this isn't a real user ID
          usersData.push(userData);
        }
      });

      console.log("Total users found:", usersData.length);
      setUsers(usersData);
    } catch (error) {
      console.error("Error refreshing users:", error);
      toast.error(`Failed to refresh users: ${error.message}`);
    } finally {
      setLoadingUsers(false);
    }
  };

  // Close user detail view and refresh users if needed
  const handleCloseUserDetail = (deleted = false) => {
    setSelectedUserId(null);
    setSelectedUserData(null);

    // If a user was deleted, refresh the list
    if (deleted) {
      refreshUsers();
    }
  };

  // Handle CSV download with admin check
  const handleDownloadCSV = async () => {
    try {
      // Verify user is admin
      const userIsAdmin = await isCurrentUserAdmin(user);
      
      if (!userIsAdmin) {
        toast.error('Unauthorized: Only admins can download user data');
        return;
      }

      // Use filteredUsers to respect current filters, or use 'users' for all users
      const dataToExport = filteredUsers.length > 0 ? filteredUsers : users;
      
      if (dataToExport.length === 0) {
        toast.error('No users to export');
        return;
      }

      // Convert to CSV
      const csv = convertToCSV(dataToExport);
      
      // Create blob and download
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      
      // Generate filename with timestamp
      const timestamp = new Date().toISOString().split('T')[0];
      link.setAttribute('href', url);
      link.setAttribute('download', `burnt-users-${timestamp}.csv`);
      link.style.visibility = 'hidden';
      
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      toast.success(`Exported ${dataToExport.length} users to CSV`);
    } catch (error) {
      console.error('Error downloading CSV:', error);
      toast.error('Failed to download CSV');
    }
  };

  return (
    <div>
      <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-4">User Management (Candidates)</h2>

      {/* Metrics Section - Compact Layout */}
      <div className="bg-white dark:bg-gray-900 shadow rounded-lg mb-6">
        <div className="px-4 py-3 sm:px-6">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-x-6 gap-y-3">
            {/* New Waitlist */}
            <div>
              <div className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">New Waitlist</div>
              <div className="mt-1 flex items-baseline gap-2">
                <div className="flex items-baseline gap-1">
                  <div className="text-2xl font-semibold text-gray-900 dark:text-gray-100">
                    {loadingMetrics ? (
                      <div className="h-7 w-12 bg-gray-200 dark:bg-gray-700 rounded animate-pulse"></div>
                    ) : (
                      metrics.newWaitlistCount30Days ?? 'N/A'
                    )}
                  </div>
                  <span className="text-xs text-gray-400 dark:text-gray-500 font-medium">30d</span>
                </div>
                <div className="text-sm text-gray-500 dark:text-gray-400">
                  {loadingMetrics ? (
                    <div className="h-5 w-8 bg-gray-200 dark:bg-gray-700 rounded animate-pulse"></div>
                  ) : (
                    <span className="font-medium">{metrics.newWaitlistCount24Hours}</span>
                  )}
                  <span className="ml-1">today</span>
                </div>
              </div>
            </div>

            {/* New Users */}
            <div>
              <div className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">New Users</div>
              <div className="mt-1 flex items-baseline gap-2">
                <div className="flex items-baseline gap-1">
                  <div className="text-2xl font-semibold text-gray-900 dark:text-gray-100">
                    {loadingMetrics ? (
                      <div className="h-7 w-12 bg-gray-200 dark:bg-gray-700 rounded animate-pulse"></div>
                    ) : (
                      metrics.newUserCount30Days ?? 'N/A'
                    )}
                  </div>
                  <span className="text-xs text-gray-400 dark:text-gray-500 font-medium">30d</span>
                </div>
                <div className="text-sm text-gray-500 dark:text-gray-400">
                  {loadingMetrics ? (
                    <div className="h-5 w-8 bg-gray-200 dark:bg-gray-700 rounded animate-pulse"></div>
                  ) : (
                    <span className="font-medium">{metrics.newUserCount24Hours}</span>
                  )}
                  <span className="ml-1">today</span>
                </div>
              </div>
            </div>

            {/* Forum Activity */}
            <div className="col-span-2 sm:col-span-2">
              <div className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">Forum Activity</div>
              <div className="mt-1 flex items-baseline gap-2">
                <div className="text-2xl font-semibold text-gray-900 dark:text-gray-100">
                  {loadingMetrics ? (
                    <div className="h-7 w-12 bg-gray-200 dark:bg-gray-700 rounded animate-pulse"></div>
                  ) : (
                    `${metrics.usersWithForumPosts.percentage}%`
                  )}
                </div>
                <div className="text-sm text-gray-500 dark:text-gray-400">
                  {loadingMetrics ? (
                    <div className="h-5 w-24 bg-gray-200 dark:bg-gray-700 rounded animate-pulse"></div>
                  ) : (
                    `${metrics.usersWithForumPosts.count} of ${metrics.usersWithForumPosts.total} users`
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* User List Section */}
      <div className="bg-white dark:bg-gray-900 shadow overflow-hidden sm:rounded-lg">
        <div className="px-4 py-5 sm:px-6 flex justify-between items-center">
          <div>
            <h3 className="text-lg leading-6 font-medium text-gray-900 dark:text-gray-100">Candidates</h3>
            <p className="mt-1 max-w-2xl text-sm text-gray-500 dark:text-gray-400">
              All users registered on the platform.
            </p>
          </div>
          <div>
            <button
              onClick={handleDownloadCSV}
              disabled={loadingUsers || users.length === 0}
              className="inline-flex items-center gap-x-2 rounded-md bg-purple-600 px-3.5 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-purple-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-purple-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5M16.5 12L12 16.5m0 0L7.5 12m4.5 4.5V3" />
              </svg>
              Download CSV
            </button>
          </div>
        </div>

        {/* Search and Filter Controls */}
        <div className="px-4 py-5 sm:px-6 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800">
          <div className="space-y-4">
            {/* Search Bar & Clear Filters */}
            <div className="flex flex-col sm:flex-row gap-3 items-stretch sm:items-center">
              <div className="relative flex-1 max-w-xl">
                <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-400 dark:text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                </div>
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search by name or UID..."
                  className="focus:ring-2 focus:ring-purple-500 focus:border-purple-500 block w-full pl-11 pr-10 py-2 text-sm border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded-md shadow-xs transition-all placeholder:text-gray-400 dark:placeholder:text-gray-500"
                />
                {searchTerm && (
                  <button
                    onClick={() => setSearchTerm('')}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                )}
              </div>

              {/* Clear All Filters Button */}
              {(locationFilter.length > 0 || jobSearchStatusFilter.length > 0 || hasLinkedInFilter.length > 0 || statusFilter.length > 0 || searchTerm || showNewUsersOnly || showOrphanedUsers || showArchivedUsers) && (
                <button
                  onClick={() => {
                    setLocationFilter([]);
                    setJobSearchStatusFilter([]);
                    setHasLinkedInFilter([]);
                    setStatusFilter([]);
                    setSearchTerm('');
                    setShowNewUsersOnly(false);
                    setShowOrphanedUsers(false);
                    setShowArchivedUsers(false);
                  }}
                  className="px-3 py-2 text-sm font-semibold text-gray-900 dark:text-gray-100 bg-white dark:bg-gray-700 inset-ring-1 inset-ring-gray-300 dark:inset-ring-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-600 shadow-xs transition-colors"
                >
                  Clear All
                </button>
              )}
            </div>

            {/* Filter Dropdowns - Organized in Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
              {/* Location Filter */}
              <MultiSelectCombobox
                label="Location"
                options={getUniqueLocations()}
                selectedValues={locationFilter}
                onChange={setLocationFilter}
                placeholder="All Locations"
                icon={(props) => (
                  <svg {...props} fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
                  </svg>
                )}
              />

              {/* Job Search Status Filter */}
              <MultiSelectCombobox
                label="Job Search"
                options={[
                  { value: 'actively-looking', label: 'Actively Looking' },
                  { value: 'open-to-opportunities', label: 'Open to Opportunities' },
                  { value: 'not-interested', label: 'Not Interested' }
                ]}
                selectedValues={jobSearchStatusFilter}
                onChange={setJobSearchStatusFilter}
                placeholder="All Statuses"
                icon={(props) => (
                  <svg {...props} fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M6 3.75A2.75 2.75 0 018.75 1h2.5A2.75 2.75 0 0114 3.75v.443c.572.055 1.14.122 1.706.2C17.053 4.582 18 5.75 18 7.07v3.469c0 1.126-.694 2.191-1.83 2.54-1.952.599-4.024.921-6.17.921s-4.219-.322-6.17-.921C2.694 12.73 2 11.665 2 10.539V7.07c0-1.321.947-2.489 2.294-2.676A41.047 41.047 0 016 4.193V3.75zm6.5 0v.325a41.622 41.622 0 00-5 0V3.75c0-.69.56-1.25 1.25-1.25h2.5c.69 0 1.25.56 1.25 1.25zM10 10a1 1 0 00-1 1v.01a1 1 0 001 1h.01a1 1 0 001-1V11a1 1 0 00-1-1H10z" clipRule="evenodd" />
                    <path d="M3 15.055v-.684c.126.053.255.1.39.142 2.092.642 4.313.987 6.61.987 2.297 0 4.518-.345 6.61-.987.135-.041.264-.089.39-.142v.684c0 1.347-.985 2.53-2.363 2.686a41.454 41.454 0 01-9.274 0C3.985 17.585 3 16.402 3 15.055z" />
                  </svg>
                )}
              />

              {/* LinkedIn Filter */}
              <MultiSelectCombobox
                label="LinkedIn"
                options={[
                  { value: 'yes', label: 'Has LinkedIn' },
                  { value: 'no', label: 'No LinkedIn' }
                ]}
                selectedValues={hasLinkedInFilter}
                onChange={setHasLinkedInFilter}
                placeholder="All Users"
                icon={(props) => (
                  <svg {...props} fill="currentColor" viewBox="0 0 24 24">
                    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
                  </svg>
                )}
              />

              {/* Status Filter */}
              <MultiSelectCombobox
                label="User Status"
                options={getUniqueStatuses()}
                selectedValues={statusFilter}
                onChange={setStatusFilter}
                placeholder="All Statuses"
                icon={(props) => (
                  <svg {...props} fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
                  </svg>
                )}
              />
            </div>

            {/* Quick Filter Buttons */}
            <div className="flex flex-wrap gap-2">
              {/* New Users Filter Button */}
              <button
                onClick={() => setShowNewUsersOnly(!showNewUsersOnly)}
                className={`inline-flex items-center gap-x-1.5 rounded-md px-3 py-2 text-sm font-semibold shadow-xs transition-colors ${showNewUsersOnly
                  ? 'bg-purple-600 text-white hover:bg-purple-500'
                  : 'bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 inset-ring-1 inset-ring-gray-300 dark:inset-ring-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600'
                  }`}
              >
                New Users ({recentUsers.length})
              </button>

              {/* Orphaned Users Filter Button */}
              <button
                onClick={() => setShowOrphanedUsers(!showOrphanedUsers)}
                className={`inline-flex items-center gap-x-1.5 rounded-md px-3 py-2 text-sm font-semibold shadow-xs transition-colors ${showOrphanedUsers
                  ? 'bg-orange-600 text-white hover:bg-orange-500'
                  : 'bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 inset-ring-1 inset-ring-gray-300 dark:inset-ring-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600'
                  }`}
              >
                Orphaned
              </button>

              {/* Archived Users Filter Button */}
              <button
                onClick={() => setShowArchivedUsers(!showArchivedUsers)}
                className={`inline-flex items-center gap-x-1.5 rounded-md px-3 py-2 text-sm font-semibold shadow-xs transition-colors ${showArchivedUsers
                  ? 'bg-amber-600 text-white hover:bg-amber-500'
                  : 'bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 inset-ring-1 inset-ring-gray-300 dark:inset-ring-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600'
                  }`}
              >
                Archived
              </button>
            </div>

            {/* Results Count & Active Filter Pills */}
            <div className="flex items-center justify-between pt-3 border-t border-gray-200 dark:border-gray-700">
              <div className="text-sm text-gray-700 dark:text-gray-300">
                {loadingUsers ? (
                  <span>Loading...</span>
                ) : (
                  <span>
                    Showing <span className="font-medium text-gray-900 dark:text-gray-100">{filteredUsers.length}</span> of <span className="font-medium text-gray-900 dark:text-gray-100">{users.length}</span> users
                  </span>
                )}
              </div>

            </div>
          </div>
        </div>

        <UserListTable
          users={filteredUsers}
          isLoading={loadingUsers}
          onUserSelect={handleUserSelect}
          sortField={sortField}
          sortDirection={sortDirection}
          onSort={handleSort}
        />
      </div>

      {/* User Detail Modal */}
      {selectedUserId && (
        <UserDetailView
          user={selectedUserData}
          isLoading={loadingUserDetail}
          onClose={(deleted) => handleCloseUserDetail(deleted)}
        />
      )}
    </div>
  );
} 